import {
  Component,
  Input,
  input,
  output,
  ViewEncapsulation,
  forwardRef,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  inject,
  viewChild,
  ElementRef,
  AfterViewInit
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { ButtonComponent } from '@infinitform/shared-ui';
import { InputNumberDirective } from '@infinitform/shared-ui';

@Component({
    selector: 'app-input',
    templateUrl: './input.component.html',
    styleUrls: ['./input.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputComponent),
            multi: true
        }
    ],
    imports: [FormsModule, ButtonComponent, InputNumberDirective]
})
export class InputComponent implements ControlValueAccessor, OnChanges, AfterViewInit {
  private cdr = inject(ChangeDetectorRef);

  placeholder = input('');
  @Input() disabled = false;
  readonly = input(false);
  min = input(-Infinity);
  max = input(Infinity);
  step = input(1);
  icon = input<'search' | 'email' | 'lock' | 'user' | false>(false);
  @Input() value: string | number | null = '';
  type = input<'text' | 'textarea' | 'password' | 'email' | 'number' | 'tel' | 'url' | 'date'>('text');
  size = input<'sm' | 'md' | 'lg' | 'xs'>('md');
  button = input<{ label: string; variant?: string; size?: string; disabled?: boolean } | undefined>(undefined);
  fakeButton = input(false);
  grouped = input(false);
  autoResize = input<{ enabled: boolean; maxHeight?: number; minHeight?: number }>({ enabled: false });
  buttonClick = output<void>();

  gapAndButtonMargin = input(false);
  suffix = input<string>('');
  suffixHint = input<string>('');

  textareaElement = viewChild<ElementRef<HTMLTextAreaElement>>('textareaElement');

  private _value: string = '';
  private onChange = (value: unknown) => {};
  private onTouched = () => {};
  private lastEmittedValue: unknown;

  // Password visibility toggle
  showPassword = false;

  get internalValue(): string {
    return this._value;
  }

  set internalValue(val: string | number) {
    const stringVal = String(val);
    if (stringVal !== this._value) {
      this._value = stringVal;

      // For number inputs, convert to number before emitting
      let emitValue: unknown = val;
      if (this.type() === 'number') {
        // Parse the string value to a number
        const numVal = parseFloat(stringVal);
        emitValue = isNaN(numVal) ? 0 : numVal;
      }

      // Only emit if value actually changed
      if (emitValue !== this.lastEmittedValue) {
        this.lastEmittedValue = emitValue;
        this.onChange(emitValue);
      }

      this.cdr.detectChanges();
    }
  }

  writeValue(value: unknown): void {
    this._value = value != null ? String(value) : '';
    this.lastEmittedValue = value; // Track the value to avoid re-emitting
    this.cdr.detectChanges();
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.detectChanges();
  }

  onInput(e: Event): void {
    const target = e.target as HTMLInputElement;
    this.internalValue = target.value;

    // Auto-resize textarea if enabled
    if (this.type() === 'textarea' && this.autoResize().enabled) {
      this.autoResizeTextarea();
    }
  }

  onBlur(): void {
    this.onTouched();
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  get actualInputType(): string {
    if (this.type() === 'password') {
      return this.showPassword ? 'text' : 'password';
    }
    return this.type();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] && changes['value'].currentValue !== this._value) {
      const incoming = changes['value'].currentValue;
      this._value = incoming != null ? String(incoming) : '';
      this.cdr.detectChanges();
    }
  }

  onButtonClick(): void {
    this.buttonClick.emit();
  }

  ngAfterViewInit(): void {
    // Initialize textarea height if auto-resize is enabled
    if (this.type() === 'textarea' && this.autoResize().enabled) {
      setTimeout(() => this.autoResizeTextarea(), 0);
    }
  }

  private autoResizeTextarea(): void {
    if (!this.textareaElement()?.nativeElement || !this.autoResize().enabled) {
      return;
    }

    const textarea = this.textareaElement()!.nativeElement;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';

    // Calculate the new height
    const scrollHeight = textarea.scrollHeight;
    const resize = this.autoResize();
    const minHeight = resize.minHeight || 40; // Default minimum height
    const maxHeight = resize.maxHeight || 200; // Default maximum height

    // Set the height within the specified limits
    const newHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight));
    textarea.style.height = `${newHeight}px`;

    // Show scrollbar if content exceeds max height
    textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
  }
}
